export const ADMIN_RESERVED_SEGMENTS = new Set([
  "org",
  "platform",
  "profile",
  "auth",
  "login",
  "callback",
]);

const ORGANIZATION_RESERVED_SLUGS = new Set([
  "www", "app", "api", "admin", "mail", "email", "support", "help",
  "blog", "docs", "status", "cdn", "static", "assets", "dev", "staging",
  "test", "demo", "preview", "beta", "auth", "login", "register",
]);

const ORGANIZATION_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;
const SAFE_RELATIVE_ROUTE_PATTERN = /^[a-z0-9-]+(?:\/[a-z0-9-]+)*$/;

const normalizeOrgSlug = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (!ORGANIZATION_SLUG_PATTERN.test(normalized)) {
    throw new Error("Organization slug is invalid");
  }
  // `demo` predates the reserved-slug contract and remains a live tenant.
  // Keep all other control-plane names blocked while preserving that legacy route.
  if (ORGANIZATION_RESERVED_SLUGS.has(normalized) && normalized !== "demo") {
    throw new Error("Organization slug is reserved");
  }
  return normalized;
};

export const adminWorkspaceIdBase = (orgId) => {
  if (typeof orgId !== "string" || !/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(orgId)) throw new Error("Organization ID is invalid");
  return `/workspace/organizations/${orgId}`;
};

export const adminOrgBase = (orgSlug) =>
  `/org/${normalizeOrgSlug(orgSlug)}`;

export const adminOrgRoute = (orgSlug, suffix = "today", workspaceOrgId) => {
  const normalizedSuffix = String(suffix).trim();
  if (!SAFE_RELATIVE_ROUTE_PATTERN.test(normalizedSuffix)) {
    throw new Error("Admin route suffix is invalid");
  }
  return `${workspaceOrgId !== undefined ? adminWorkspaceIdBase(workspaceOrgId) : adminOrgBase(orgSlug)}/${normalizedSuffix}`;
};

const SAFE_RESOURCE_SEGMENT = "[A-Za-z0-9_-]+";
const ORG_ROUTE_PATTERNS = [
  /^(website|profile|connect(\/(connections|review|history|rules))?)$/,
  /^(today|programs|people|operations|operations\/(scheduling|facilities)|finance|finance\/(invoices|payment-plans|promo-codes)|communications|communications\/social|support|marketplace|extensions|extensions\/(wstl|tournaments|leagues|developer)|settings|settings\/import)$/,
  new RegExp(`^programs/(new/${SAFE_RESOURCE_SEGMENT}|${SAFE_RESOURCE_SEGMENT}(/edit)?)$`),
  /^people\/(directory|enrollments|households|coaches)$/,
  new RegExp(`^people/households/${SAFE_RESOURCE_SEGMENT}$`),
  new RegExp(`^finance/reports(/${SAFE_RESOURCE_SEGMENT})*$`),
];
const PLATFORM_ROUTE_PATTERNS = [
  /^dashboard$/,
  /^organizations$/,
  new RegExp(`^organizations/${SAFE_RESOURCE_SEGMENT}$`),
  /^users$/,
  new RegExp(`^settings(/${SAFE_RESOURCE_SEGMENT})*$`),
  /^marketing\/demo-funnel$/,
  /^donations$/,
];

const SAFE_PLATFORM_RESOURCE_PATTERN = /^[A-Za-z0-9_-]+$/;
const SAFE_PLATFORM_SETTINGS_PATH_PATTERN = /^[A-Za-z0-9_-]+(?:\/[A-Za-z0-9_-]+)*$/;

export const legacyPlatformAdminDestination = (pathname) => {
  const path = String(pathname || "").replace(/\/$/, "");
  if (path === "/admin") return "/platform/dashboard";
  if (path === "/admin/organizations") return "/platform/organizations";
  if (path === "/admin/users") return "/platform/users";
  if (path === "/admin/donations") return "/platform/donations";
  if (path === "/admin/platform") return "/platform/settings";
  if (path === "/admin/marketing/demo-funnel") return "/platform/marketing/demo-funnel";

  const organizationPrefix = "/admin/organizations/";
  if (path.startsWith(organizationPrefix)) {
    const organizationId = path.slice(organizationPrefix.length);
    return SAFE_PLATFORM_RESOURCE_PATTERN.test(organizationId)
      ? `/platform/organizations/${organizationId}`
      : null;
  }

  const settingsPrefix = "/admin/platform/";
  if (path.startsWith(settingsPrefix)) {
    const suffix = path.slice(settingsPrefix.length);
    return SAFE_PLATFORM_SETTINGS_PATH_PATTERN.test(suffix)
      ? `/platform/settings/${suffix}`
      : null;
  }
  return null;
};

// Platform aliases are host redirects, never tenant workflows.
export const isLegacyOrganizationAdminPath = (pathname) => {
  const path = String(pathname || "").replace(/\/$/, "");
  return /^\/admin(?:\/|$)/.test(path) && (path === "/admin" || !legacyPlatformAdminDestination(path));
};

export const matchAdminPortalRoute = (pathname, orgSlug, mode = "organization", workspaceOrgId) => {
  const prefix = mode === "platform" ? "/platform/" : `${workspaceOrgId !== undefined ? adminWorkspaceIdBase(workspaceOrgId) : adminOrgBase(orgSlug)}/`;
  if (!pathname.startsWith(prefix)) return null;
  const suffix = pathname.slice(prefix.length).replace(/\/$/, "");
  const patterns = mode === "platform" ? PLATFORM_ROUTE_PATTERNS : ORG_ROUTE_PATTERNS;
  if (!suffix || !patterns.some((pattern) => pattern.test(suffix))) return null;
  return suffix.split("/")[0];
};

export const ADMIN_ORG_NAVIGATION = [
  {
    label: "Today",
    group: "Overview",
    icon: "layout-dashboard",
    destination: "today",
    match: ["today"],
  },
  {
    label: "Programs",
    group: "Programs",
    icon: "clipboard-list",
    destination: "programs",
    match: ["programs"],
  },
  {
    label: "People",
    group: "Members",
    icon: "users",
    destination: "people",
    match: ["people"],
    children: [
      { label: "Directory", destination: "people/directory" },
      { label: "Households", destination: "people/households" },
      { label: "Enrollment history", destination: "people/enrollments" },
    ],
  },
  {
    label: "Operations",
    group: "Operations",
    icon: "calendar",
    destination: "operations",
    match: ["operations"],
    children: [
      { label: "Scheduling", destination: "operations/scheduling" },
      { label: "Facilities", destination: "operations/facilities" },
    ],
  },
  {
    label: "Finance",
    group: "Finance",
    icon: "dollar-sign",
    destination: "finance",
    match: ["finance"],
    children: [
      { label: "Overview", destination: "finance" },
      { label: "Invoices", destination: "finance/invoices" },
      { label: "Payment plans", destination: "finance/payment-plans" },
      { label: "Promotion codes", destination: "finance/promo-codes" },
      { label: "Reports", destination: "finance/reports" },
    ],
  },
  {
    label: "Communications",
    group: "Communications",
    icon: "message-square",
    destination: "communications",
    match: ["communications", "support"],
    children: [
      { label: "Messaging", destination: "communications" },
      { label: "Social", destination: "communications/social" },
      { label: "Support", destination: "support" },
    ],
  },
  {
    label: "Marketplace",
    group: "Extend",
    icon: "store",
    destination: "marketplace",
    match: ["marketplace", "extensions"],
  },
  {
    label: "Settings",
    group: "Administration",
    icon: "settings",
    destination: "settings",
    match: ["settings"],
  },
];

export const ADMIN_PLATFORM_NAVIGATION = [
  { label: "Dashboard", group: "Platform", icon: "layout-dashboard", destination: "/platform/dashboard" },
  { label: "Organizations", group: "Platform", icon: "building", destination: "/platform/organizations" },
  { label: "Users", group: "Platform", icon: "users", destination: "/platform/users" },
  { label: "Platform settings", group: "Administration", icon: "settings", destination: "/platform/settings" },
  { label: "Demo Funnel", group: "Marketing", icon: "megaphone", destination: "/platform/marketing/demo-funnel" },
  { label: "Donations", group: "Finance", icon: "heart-handshake", destination: "/platform/donations" },
];
